import { useState } from 'react';
import { ILicenceTemplateApplicationProcessResponse } from 'models/licenceConfigurator';
import { getLicenceTemplateApplicationProccess } from 'requests/licenceConfigurator';
import { EDictionaryTypes } from 'models/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { getLegalEntityLicenceDetails } from 'requests/legalEntities';
import { ILegalEntityLicenceDetails } from 'models/legalEntity';
import { ProcessTypes } from 'constants/ProcessTypes';
import { getCaseRenewData, getCaseVaryData, renewLicenceCreateCase } from 'requests/licence';
import { ICaseFormValues } from 'models/cases';
import { useAlert } from 'models/alertContext';
import { useTranslations } from 'hooks/useTranslations';
import { ActionTypes } from 'state/actions/alert';
import { EAlertVariants } from 'components/atoms/Alert';

export const useRenewLicence = () => {
  const { t } = useTranslations();
  const { setAlert } = useAlert();
  const [licencePrices, setLicencePrices] = useState<
    ILicenceTemplateApplicationProcessResponse[]
  >([]);
  const {
    getApplicationProcessTypes,
    applicationProcessTypes,
    getWeaponLicenceStatus,
    weaponLicenceStatus,
    getWeaponLicenceCategory,
    weaponLicenceCategory,
    getWeaponLicenceCompetency,
    weaponLicenceCompetency,
    getWeaponLicenceGoodReason,
    weaponLicenceGoodReason,
    getWeaponLicencePaymentStatus,
    weaponLicencePaymentStatus,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    region,
    country,
    addressType,
    getRegion,
    getCountry,
    getAddressType,
    organisationStateType,
    getOrganisationStateType,
    organisationCivilianType,
    getOrganisationCivilianType,
    organisationCategory,
    getOrganisationCategory,
    weaponLicenceCondition,
    getWeaponLicenceCondition,
    attachmentDocumentTypeData,
    getAttachmentDocumentTypeData,
    gender,
    getGender,
    civilianPrefix,
    getCivilianPrefix,
    identificationDocumentType,
    getIdentificationDocumentType,
    nationality,
    getNationality,
    state,
    getState,
    firearmType,
    getFirearmType,
    make,
    getMake,
    model,
    getModel,
    caliber,
    getCaliber,
    firearmAction,
    getFirearmAction,
  } = useDictionary();
  const [licenceDetails, setLicenceDetails] = useState<ILegalEntityLicenceDetails | null>(
    null,
  );

  const getLicenceApplicationProccessData = async (
    id: string,
  ): Promise<ILicenceTemplateApplicationProcessResponse[] | null> => {
    const data = await getLicenceTemplateApplicationProccess(id);

    if (data) {
      const renewalPrices = data.filter(
        (item) => item.processTypeUniqueId === ProcessTypes.RENEWAL,
      );
      setLicencePrices(renewalPrices);
      return renewalPrices;
    }
    setLicencePrices([]);
    return null;
  };

  const loadDictionaries = async (types: EDictionaryTypes[]) => {
    if (types.includes(EDictionaryTypes.applicationProcessTypes)) {
      getApplicationProcessTypes();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceStatus)) {
      getWeaponLicenceStatus();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceCategory)) {
      getWeaponLicenceCategory();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceCompetency)) {
      getWeaponLicenceCompetency();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceGoodReason)) {
      getWeaponLicenceGoodReason();
    }
    if (types.includes(EDictionaryTypes.weaponLicencePaymentStatus)) {
      getWeaponLicencePaymentStatus();
    }
    if (types.includes(EDictionaryTypes.weaponLicencingAuthority)) {
      getWeaponLicencingAuthority();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceRegion)) {
      getRegion();
    }
    if (types.includes(EDictionaryTypes.Country)) {
      getCountry();
    }
    if (types.includes(EDictionaryTypes.AddressType)) {
      getAddressType();
    }
    if (types.includes(EDictionaryTypes.OrganisationStateType)) {
      getOrganisationStateType();
    }
    if (types.includes(EDictionaryTypes.OrganisationCivilianType)) {
      getOrganisationCivilianType();
    }
    if (types.includes(EDictionaryTypes.OrganisationCategory)) {
      getOrganisationCategory();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceCondition)) {
      getWeaponLicenceCondition();
    }
    if (types.includes(EDictionaryTypes.attachmentDocumentTypeData)) {
      getAttachmentDocumentTypeData();
    }
    if (types.includes(EDictionaryTypes.Gender)) {
      getGender();
    }
    if (types.includes(EDictionaryTypes.civilianPrefix)) {
      getCivilianPrefix();
    }
    if (types.includes(EDictionaryTypes.IdentificationDocumentType)) {
      getIdentificationDocumentType();
    }
    if (types.includes(EDictionaryTypes.nationality)) {
      getNationality();
    }
    if (types.includes(EDictionaryTypes.State)) {
      getState();
    }
    if (types.includes(EDictionaryTypes.Make)) {
      getMake();
    }
    if (types.includes(EDictionaryTypes.Model)) {
      getModel();
    }
    if (types.includes(EDictionaryTypes.Caliber)) {
      getCaliber();
    }
    if (types.includes(EDictionaryTypes.FirearmAction)) {
      getFirearmAction();
    }
    if (types.includes(EDictionaryTypes.FirearmType)) {
      getFirearmType();
    }
  };

  const getLicenceData = async (id: string) => {
    const legalEntityLicencesData = await getLegalEntityLicenceDetails(id);
    if (legalEntityLicencesData) {
      setLicenceDetails(legalEntityLicencesData);
    } else {
      setLicenceDetails(null);
    }
  };

  const getCaseRenewForm = async (id: string) => {
    return await getCaseRenewData(id);
  };
  const getCaseVaryForm = async (id: string) => {
    return await getCaseVaryData(id);
  };

  const renewLicence = async (body: ICaseFormValues, licenceId: string) => {
    try {
      const { documents, documentTemplateUniqueIds, documentsFilenames } = Object.entries(
        body.documents ?? [],
      ).reduce(
        (acc, [key, files]) => {
          files.forEach((file, index) => {
            acc.documents.push(file);
            acc.documentTemplateUniqueIds.push(key);
            if (body.documentsFilenames) {
              acc.documentsFilenames.push(body.documentsFilenames[key][index]);
            }
          });
          return acc;
        },
        {
          documents: [] as File[],
          documentTemplateUniqueIds: [] as string[],
          documentsFilenames: [] as string[],
        },
      );

      const response = await renewLicenceCreateCase({
        request: {
          legalUniqueId: body.legalUniqueId,
          licenceAuthorityUniqueId: body.licenceAuthorityUniqueId,
          weaponLicenceUniqueId: licenceId,
          documentTemplateUniqueIds: documentTemplateUniqueIds,
          documentsFilenames: documentsFilenames,
          paymentStatusUniqueId: body.paymentStatusUniqueId,
        },
        applicationFiles: body.applicationFiles,
        documents: documents ?? null,
      });
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.createCases.success.text'),
        });
        return response.data.uniqueId;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  return {
    getLicenceApplicationProccessData,
    licencePrices,
    loadDictionaries,
    licenceDetails,
    getLicenceData,
    applicationProcessTypes,
    weaponLicenceStatus,
    weaponLicencePaymentStatus,
    weaponLicencingAuthority,
    region,
    country,
    organisationCategory,
    organisationCivilianType,
    organisationStateType,
    weaponLicenceCompetency,
    weaponLicenceCategory,
    weaponLicenceCondition,
    attachmentDocumentTypeData,
    addressType,
    weaponLicenceGoodReason,
    gender,
    civilianPrefix,
    identificationDocumentType,
    nationality,
    state,
    firearmType,
    make,
    model,
    caliber,
    firearmAction,
    getCaseRenewForm,
    getCaseVaryForm,
    renewLicence,
  };
};
